import { useToast, useI18n, navigateTo, readonly, ref } from '#imports'
import { useUserStore } from '~/store/user'
import { Capacitor } from '@capacitor/core'
import { openPendingTab, type PendingTab } from '~/utils/pendingTab'

/**
 * Composable for SSO authentication to WHMCS
 * Provides loading state management and error handling
 */
export function useWhmcsSso() {
  const userStore = useUserStore()
  const loading = ref(false)
  // Captured at composable init: useI18n() throws outside setup context, and
  // ssoToWhmcs runs from click handlers.
  const { t } = useI18n()
  const toast = useToast()

  /**
   * Land an external URL in the tab opened for it. On the web,
   * `tab === null` means the browser refused even a tab
   * opened during the click (strict blockers, in-app browsers), so the
   * customer gets a link to press: a real link is never blocked, and the page
   * they were on (a wizard waiting for this payment) stays where it is.
   */
  function deliverToTab(tab: PendingTab | null, url: string) {
    if (tab) {
      tab.navigate(url)
      return
    }
    // The native shell pre-opens nothing and has no blocker: `window.open`
    // hands the URL to the system browser.
    if (Capacitor.isNativePlatform()) {
      window.open(url, '_blank')
      return
    }
    toast.add({
      title: t('externalTab.blockedTitle'),
      description: t('externalTab.blockedDesc'),
      color: 'warning',
      icon: 'i-lucide-external-link',
      // Stays until dismissed: a message carrying the only way forward must
      // not time out under someone reading it (WCAG 2.2.1). A link pressed
      // after its minute has passed reaches the WHMCS login, and Pay mints a
      // fresh one.
      duration: 0,
      actions: [
        {
          label: t('externalTab.openLink'),
          icon: 'i-lucide-external-link',
          color: 'warning',
          variant: 'solid',
          to: url,
          target: '_blank',
          external: true,
        },
      ],
    })
  }

  /**
   * Open a URL we already hold in a new tab. Call synchronously from a click.
   */
  function openUrlInNewTab(url: string) {
    deliverToTab(openPendingTab(t('externalTab.opening')), url)
  }

  /**
   * Performs SSO to WHMCS with a specific path
   * @param path - The WHMCS path (e.g., 'clientarea.php?action=invoices', 'supporttickets.php')
   * @param options - Optional configuration
   * @returns Promise that resolves when SSO is complete
   *
   * With `openInNewTab` (the default) the tab is opened before the sign-in
   * link is minted, so this must be called synchronously from the click —
   * see `openPendingTab`.
   */
  async function ssoToWhmcs(
    path?: string,
    options: {
      openInNewTab?: boolean
      showToast?: boolean
    } = {}
  ): Promise<void> {
    const { openInNewTab = true, showToast = false } = options

    const tab = openInNewTab ? openPendingTab(t('externalTab.opening')) : null
    try {
      loading.value = true
      const ssoData = await userStore.ssoWhmcs(path)

      if (ssoData?.redirect_url) {
        if (openInNewTab) {
          deliverToTab(tab, ssoData.redirect_url)
        } else {
          await navigateTo(ssoData.redirect_url, { external: true })
        }

        if (showToast) {
          toast.add({
            title: t('layout.modal.description'),
            color: 'neutral'
          })
        }
      } else {
        tab?.close()
        // WHMCS answered but provisioned no token (e.g. result: "error").
        // Silently doing nothing on click reads as a dead button — surface it.
        toast.add({
          title: t('general.error.title'),
          description:
            (ssoData as any)?.message ?? 'SSO token could not be provisioned',
          color: 'error'
        })
      }
    } catch (error) {
      tab?.close()
      console.error('Error creating SSO token:', error)
      toast.add({
        title: t('general.error.title'),
        description: error instanceof Error ? error.message : 'Failed to create SSO token',
        color: 'error'
      })
    } finally {
      loading.value = false
    }
  }

  return {
    loading: readonly(loading),
    ssoToWhmcs,
    openUrlInNewTab,
  }
}
